<template>
  <div class="expandable-list" :class="expandableListClasses">
    <div
      v-for="slotPrefix of slotPrefixes"
      :key="slotPrefix"
      class="item expandable-list__item"
      :class="listItemClasses"
    >
      <div
        class="flex--space-between--center header item__header hover-transition"
        :class="getTitleClasses(slotPrefix)"
        @click="toggleItem(slotPrefix)"
      >
        <slot :name="getTitleSlotName(slotPrefix)"></slot>
        <span class="header__icon" :class="getExpandButtonClasses(slotPrefix)"> </span>
      </div>
      <transition
        @before-enter="expandAnimationBeforeEnter"
        @enter="expandAnimation"
        @before-leave="collapseAnimation"
      >
        <div v-if="getIsSectionExpanded(slotPrefix)">
          <div class="item__body u-typography-helvetica u-text u-text--s">
            <slot :name="getBodySlotName(slotPrefix)"></slot>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExpandableList',
  props: {
    initiallyExpandedSlotPrefix: {
      type: String,
      default: ''
    },
    inverted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expandedSlotPrefixes: []
    };
  },
  computed: {
    slotPrefixes() {
      return Object.keys(this.$slots)
        .filter(name => name.endsWith('title'))
        .map(this.getSlotPrefixFromName);
    },
    expandableListClasses() {
      return {
        'expandable-list--inverted': this.inverted
      };
    },
    listItemClasses() {
      return {
        'item--inverted': this.inverted
      };
    }
  },
  methods: {
    getSlotPrefixFromName(slotName) {
      const parts = slotName.split('-');

      return parts.slice(0, parts.length - 1).join('-');
    },
    getTitleSlotName(slotPrefix) {
      return `${slotPrefix}-title`;
    },
    getBodySlotName(slotPrefix) {
      return `${slotPrefix}-body`;
    },
    toggleItem(slotPrefix) {
      const shouldCollapse = this.getIsSectionExpanded(slotPrefix);

      if (shouldCollapse) {
        this.removeSlotFormExpandedList(slotPrefix);

        return;
      }

      this.expandedSlotPrefixes.push(slotPrefix);
    },
    getIsSectionExpanded(slotPrefix) {
      return this.expandedSlotPrefixes.includes(slotPrefix);
    },
    removeSlotFormExpandedList(slotPrefix) {
      const slotPrefixIndex = this.expandedSlotPrefixes.indexOf(slotPrefix);

      this.expandedSlotPrefixes.splice(slotPrefixIndex, 1);
    },
    getTitleClasses(slotPrefix) {
      return {
        'item__header--expanded': this.getIsSectionExpanded(slotPrefix),
        'item__header--inverted': this.inverted
      };
    },
    getExpandButtonClasses(slotPrefix) {
      return {
        'icon-minus': this.getIsSectionExpanded(slotPrefix),
        'icon-plus': !this.getIsSectionExpanded(slotPrefix)
      };
    },
    expandAnimationBeforeEnter(el) {
      const element = el;
      element.style.opacity = 0;
    },
    expandAnimation(el) {
      const element = el;
      const height = element.scrollHeight;
      const transition = 'height 0.3s ease, opacity 0.8s ease';

      element.style.transition = '';
      element.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        element.style.height = '0px';
        element.style.transition = transition;

        requestAnimationFrame(() => {
          element.style.height = `${height}px`;
          element.style.opacity = 1;
        });
      });
    },
    collapseAnimation(el) {
      const element = el;

      element.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      element.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        element.style.height = '0px';
        element.style.opacity = 0;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/colors.scss';
@import '~@/assets/scss/icon.scss';

.item {
  border-bottom: 1px solid var(--u-color-grey-200);
  font-style: normal;
  font-weight: 500;

  &:last-child {
    border-bottom: none;
  }

  &__header {
    font-family: var(--u-font-family-Helvetica);
    font-size: 18px;
    line-height: 27px;
    font-weight: 400;
    letter-spacing: 0.4px;
    text-align: left;
    color: var(--u-color-black-4);
  }

  &__body {
    padding: 0 0 1.2rem 0;

    color: var(--u-color-grey-700);
  }
}

.header {
  padding: 1.6rem 0;

  &:hover {
    cursor: pointer;
  }
}
</style>
